/**
 * ShippingProviderSettingsSection — per-location shipping provider picker.
 *
 * Assigns Manual, StarShipIt, Portless or YunExpress to each location. Mirrors LocationSettingsSection's per-location
 * dropdown + credential-gating pattern. Org-level provider credentials live
 * under Settings → Advanced — the "Configure …" links deep-link there.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NoteLine } from '@/components/shared/NoteLine';
import { MapPin, Loader2, AlertTriangle, RefreshCw, Truck, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { useRole } from '@/hooks/use-role';
import { toast } from 'sonner';
import {
  SHIPPING_PROVIDER_ORDER,
  SHIPPING_PROVIDER_PRESENTATION,
  isClientOnlyProvider,
} from '../constants/shipping-providers';

/** Where the org configures StarShipIt credentials (Advanced tab deep-link). */
const STARSHIPIT_CREDS_HREF = SHIPPING_PROVIDER_PRESENTATION.starshipit.credsHref ?? '/production/settings?tab=advanced';
/** Where the org configures Portless credentials (Advanced tab deep-link). */
const PORTLESS_CREDS_HREF = SHIPPING_PROVIDER_PRESENTATION.portless.credsHref ?? '/production/settings?tab=advanced';

/** Providers an operator can actually pick today (mirror of the backend enum). */
type SelectableProvider = 'manual' | 'starshipit' | 'portless' | 'yunexpress';

interface ShippingProviderSettingsSectionProps {
  /** When true, renders without the Card wrapper for embedding in accordions. */
  embedded?: boolean;
  /**
   * Console single-location mode: when set, render only this location's row
   * and omit the per-row location name (the Console section header already
   * names the location). Absent = today's full-list behavior.
   */
  locationId?: string;
  /**
   * The caller renders `YunExpressLocationSettings` for the same location
   * directly beneath this section. Its shipping-product field carries the
   * amber "choose a product" warning, so this row does not repeat it: the
   * admin reads one warning, at the control that fixes it.
   */
  yunexpressProductFieldBelow?: boolean;
}

export function ShippingProviderSettingsSection({
  embedded = false,
  locationId,
  yunexpressProductFieldBelow = false,
}: ShippingProviderSettingsSectionProps) {
  // Only the YunExpress lines are translated so far (Ningbo admins read zh);
  // the rest of this section is still English.
  const { t } = useTranslation('settings');
  // YunExpress lets Print Run fulfil inheriting clients' Shopify orders at
  // Print, so the backend accepts it from an admin only.
  const { isAdmin } = useRole();
  const admin = isAdmin();
  const yunexpressSettingsQuery = trpc.productionSettings.getYunExpressSettings.useQuery();
  const hasYunexpressCredentials = yunexpressSettingsQuery.data?.hasCredentials === true;
  const locationsQuery = trpc.productionSettings.getLocationSettings.useQuery();
  // Org-level credential presence — one query per provider for every row.
  // Drives the "Setup required" sub-line and the per-row not-configured alert.
  const starshipitSettingsQuery = trpc.productionSettings.getStarShipItSettings.useQuery();
  const hasStarshipitCredentials = starshipitSettingsQuery.data?.hasCredentials === true;
  const portlessSettingsQuery = trpc.productionSettings.getPortlessSettings.useQuery();
  const hasPortlessCredentials = portlessSettingsQuery.data?.hasCredentials === true;

  const setProviderMutation = trpc.productionSettings.setLocationShippingProvider.useMutation({
    onSuccess: () => {
      toast.success('Shipping provider updated');
      locationsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to update provider: ${error.message}`);
    },
  });

  const isLoading = locationsQuery.isLoading;
  const hasError = locationsQuery.isError;
  const allLocations = locationsQuery.data || [];
  const locations = locationId ? allLocations.filter((l) => l.id === locationId) : allLocations;

  const content = (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Choose how each location ships packed orders. Manual records shipping outside the IMS;
        StarShipIt, Portless and YunExpress print labels at the packing station. Provider credentials live
        in their sections below.
      </p>

      {/* Error State */}
      {hasError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Failed to load locations</AlertTitle>
          <AlertDescription className="flex items-center gap-2">
            {locationsQuery.error?.message || 'Unknown error'}
            <Button variant="link" className="p-0 h-auto" onClick={() => locationsQuery.refetch()}>
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8" role="status" aria-busy="true">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="ml-2 text-muted-foreground">Loading locations...</span>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !hasError && locations.length === 0 && (
        <div className="flex flex-col items-center py-8 text-center bg-muted/30 rounded-lg">
          <MapPin className="h-10 w-10 text-muted-foreground/50 mb-3" />
          <p className="font-medium">No locations configured</p>
          <p className="text-sm text-muted-foreground mb-4">
            Add locations in IMS Settings to assign a shipping provider
          </p>
          <Button variant="outline" asChild className="h-12 gap-2">
            <a href="/settings?tab=locations">
              <ExternalLink className="h-4 w-4" />
              Go to IMS Settings
            </a>
          </Button>
        </div>
      )}

      {/* Location List */}
      {!isLoading && !hasError && locations.length > 0 && (
        <div className="space-y-3" role="list" aria-label="Per-location shipping provider">
          {locations.map((location) => {
            const provider = location.shippingProvider;
            const presentation = SHIPPING_PROVIDER_PRESENTATION[provider];
            // YunExpress also needs this location's shipping product: without
            // one, packing cannot buy a label even with credentials saved.
            const yunexpressNeedsProduct = !location.yunexpressShippingMethod;
            const needsCredentials =
              (provider === 'starshipit' && !hasStarshipitCredentials) ||
              (provider === 'portless' && !hasPortlessCredentials) ||
              (provider === 'yunexpress' && !hasYunexpressCredentials);
            const needsProduct =
              provider === 'yunexpress' &&
              hasYunexpressCredentials &&
              yunexpressNeedsProduct &&
              !yunexpressProductFieldBelow;
            const credsHref = presentation.credsHref ?? STARSHIPIT_CREDS_HREF;
            const rowPending =
              setProviderMutation.isPending && setProviderMutation.variables?.locationId === location.id;
            // Card-per-row (vs the flat sibling rows) so a location's
            // not-configured warning can attach directly under it.
            return (
              <div key={location.id} role="listitem" className="overflow-hidden rounded-lg bg-muted/30">
                <div className="flex items-center justify-between p-3 transition-colors hover:bg-muted/50">
                  <div>
                    {!locationId && <p className="font-medium">{location.name}</p>}
                    <NoteLine icon={Truck} className="mt-1">
                      {presentation.label}
                    </NoteLine>
                  </div>

                  <div className="flex items-center gap-2">
                    {rowPending && (
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" aria-hidden="true" />
                    )}
                    <Select
                      value={provider}
                      onValueChange={(v) =>
                        setProviderMutation.mutate({ locationId: location.id, provider: v as SelectableProvider })
                      }
                      disabled={rowPending}
                    >
                      <SelectTrigger
                        className="h-8 w-[240px] text-xs"
                        aria-label={`Shipping provider for ${location.name}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Client-only providers (Hand over) are a per-client
                            choice at a shared location, never a location
                            default, so they are not offered here. */}
                        {SHIPPING_PROVIDER_ORDER.filter((id) => !isClientOnlyProvider(id)).map((id) => {
                          const p = SHIPPING_PROVIDER_PRESENTATION[id];
                          const comingSoon = p.status === 'coming_soon';
                          // Credentialed providers stay selectable even without
                          // creds (the admin may be configuring them in
                          // parallel); the gap is surfaced via the sub-line +
                          // the row alert below. Coming-soon providers are
                          // hard-disabled.
                          const itemNeedsSetup =
                            (id === 'starshipit' && !hasStarshipitCredentials) ||
                            (id === 'portless' && !hasPortlessCredentials) ||
                            (id === 'yunexpress' && (!hasYunexpressCredentials || yunexpressNeedsProduct));
                          // Disabled, not hidden: a location already on YunExpress
                          // still shows it as the selected value to a coordinator.
                          const adminOnly = id === 'yunexpress' && !admin;
                          const subline = adminOnly
                            ? t('production.shippingProvider.yunexpressAdminOnly')
                            : itemNeedsSetup
                              ? 'Setup required'
                              : p.tagline;
                          return (
                            <SelectItem key={id} value={id} disabled={comingSoon || adminOnly} textValue={p.label}>
                              {comingSoon ? (
                                // Glanceable "Coming soon" pill — a disabled item with only a
                                // tiny muted sub-line is easy to miss (frontend-design review).
                                <div className="flex min-w-0 items-center gap-2">
                                  <span className="truncate">{p.label}</span>
                                  <Badge
                                    variant="secondary"
                                    className="shrink-0 px-1.5 py-0 text-[10px] font-normal"
                                  >
                                    Coming soon
                                  </Badge>
                                </div>
                              ) : (
                                <div className="flex min-w-0 flex-col">
                                  <span className="truncate">{p.label}</span>
                                  <span
                                    className={`text-[10px] ${itemNeedsSetup && !adminOnly ? 'text-amber-700' : 'text-muted-foreground'}`}
                                  >
                                    {subline}
                                  </span>
                                </div>
                              )}
                            </SelectItem>
                          );
                        })}
                        {(!hasStarshipitCredentials || !hasPortlessCredentials) && (
                          <div className="flex flex-col gap-1 border-t px-2 py-1.5">
                            {!hasStarshipitCredentials && (
                              <Link
                                to={STARSHIPIT_CREDS_HREF}
                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                              >
                                Configure StarShipIt
                                <ExternalLink className="h-3 w-3" />
                              </Link>
                            )}
                            {!hasPortlessCredentials && (
                              <Link
                                to={PORTLESS_CREDS_HREF}
                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                              >
                                Configure Portless
                                <ExternalLink className="h-3 w-3" />
                              </Link>
                            )}
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Not-configured warning — non-blocking; packers fall back to
                    Mark complete until org creds are saved. One per affected row. */}
                {needsCredentials && (
                  <div className="px-3 pb-3">
                    <Alert
                      variant="default"
                      className="border-amber-300 bg-amber-50 text-amber-900 [&>svg]:text-amber-700"
                    >
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        {presentation.label} is set for {location.name} but no credentials are saved. This location
                        falls back to Mark Complete until you{' '}
                        <Link to={credsHref} className="underline hover:text-amber-950">
                          add {presentation.label} credentials
                        </Link>
                        .
                      </AlertDescription>
                    </Alert>
                  </div>
                )}
                {needsProduct && (
                  <div className="px-3 pb-3">
                    <Alert
                      variant="default"
                      className="border-amber-300 bg-amber-50 text-amber-900 [&>svg]:text-amber-700"
                    >
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        {t('production.shippingProvider.yunexpressNoProduct', { location: location.name })}
                      </AlertDescription>
                    </Alert>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Info about IMS locations */}
      {!isLoading && !hasError && locations.length > 0 && (
        <p className="pt-2 text-xs text-muted-foreground">
          To add or remove locations, go to{' '}
          <a href="/settings?tab=locations" className="underline hover:text-foreground">
            IMS Settings - Locations
          </a>
        </p>
      )}

      {/* ARIA live region */}
      <div role="status" aria-live="polite" className="sr-only">
        {setProviderMutation.isSuccess && 'Shipping provider updated'}
      </div>
    </div>
  );

  if (embedded) return content;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Truck className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <CardTitle className="text-lg">Shipping provider</CardTitle>
        </div>
        <CardDescription>Choose how each location ships packed orders.</CardDescription>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
